import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import PolizzaWizard, { WizardForm } from '../../components/PolizzaWizard';
import { apiFetch, apiDownload } from '../../lib/api';
import { Polizza, Societa, Sede, AllegatoPolizza, StatoPolizza } from '../../types';

const STATO_BADGE: Record<StatoPolizza, string> = {
  VERDE:   'bop-badge--success',
  GIALLO:  'bop-badge--warning',
  ARANCIO: 'bop-badge--warning',
  ROSSO:   'bop-badge--danger',
  SCADUTA: 'bop-badge--neutral',
};

export default function DettaglioPolizzaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [polizza,    setPolizza]    = useState<Polizza | null>(null);
  const [societa,    setSocieta]    = useState<Societa[]>([]);
  const [sediAll,    setSediAll]    = useState<Sede[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [deleting,   setDeleting]   = useState(false);

  const load = async () => {
    const [polRes, socRes, sediRes] = await Promise.all([
      apiFetch<{ polizza: Polizza }>(`/api/assicurazioni/polizze/${id}`),
      apiFetch<{ societa: Societa[] }>('/api/societa'),
      apiFetch<{ sedi: Sede[] }>('/api/sedi'),
    ]);
    setPolizza(polRes.polizza);
    setSocieta(socRes.societa);
    setSediAll(sediRes.sedi);
  };

  useEffect(() => {
    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Wizard pre-popolato per edit ─────────────────────────────────────────
  const buildInitialWizard = (): WizardForm | undefined => {
    if (!polizza) return undefined;

    const sedeIds = (polizza.sediDettaglio || []).map((s) => s.sedeId);

    // Ricostruisce rischiMassimali: societaSederischioId -> massimale string
    const rischiMassimali: Record<number, string> = {};
    for (const sede of polizza.sediDettaglio || []) {
      for (const r of sede.rischi) {
        if (r.massimale != null) {
          rischiMassimali[r.societa_sederischio_id] = String(r.massimale);
        }
      }
    }

    return {
      societaId:       String(polizza.societaId),
      sedeIds,
      rischiMassimali,
      numero:          polizza.numero,
      compagnia:       polizza.compagnia,
      agente:          polizza.agente || '',
      dataInizio:      polizza.dataInizio.split('T')[0],
      dataFine:        polizza.dataFine.split('T')[0],
      tacitaRinnovo:   polizza.tacitaRinnovo,
      importoTotale:   polizza.importoTotale != null ? String(polizza.importoTotale) : '',
      note:            polizza.note || '',
    };
  };

  const handleSaved = () => {
    setShowWizard(false);
    setLoading(true);
    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
  };

  const handleDelete = async () => {
    if (!polizza) return;
    if (!window.confirm(`Eliminare la polizza "${polizza.numero}"? L'operazione è irreversibile.`)) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/assicurazioni/polizze/${id}`, { method: 'DELETE' });
      navigate('/assicurazioni/polizze');
    } catch (e: any) { setError(e.message); setDeleting(false); }
  };

  const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
  const fmtEuro = (v?: number | null) =>
    v != null
      ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(v))
      : '—';

  if (loading) {
    return <Layout><div className="inline-loading">Caricamento…</div></Layout>;
  }
  if (!polizza) {
    return <Layout><div className="inline-error">{error || 'Polizza non trovata'}</div></Layout>;
  }

  const initialWizard = buildInitialWizard();

  return (
    <Layout>
      <BopBackLink to="/assicurazioni/polizze" label="Torna alle polizze" />
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Polizza {polizza.numero}</h2>
          <p>{polizza.compagnia}{polizza.agente ? ` — Agente: ${polizza.agente}` : ''}</p>
        </div>
        <div className="bop-flex">
          <button className="bop-btn bop-btn-secondary" onClick={() => setShowWizard(true)}>
            Modifica
          </button>
          <button className="bop-btn bop-btn-danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminazione…' : 'Elimina'}
          </button>
        </div>
      </div>

      {error && <div className="inline-error">{error}</div>}

      {/* ── Dati generali ────────────────────────────────────────────────── */}
      <div className="bop-card bop-polizza-det__card-padded">
        <div className="detail-grid">
          {[
            { label: 'Numero polizza',  value: polizza.numero },
            { label: 'Compagnia',       value: polizza.compagnia },
            { label: 'Agente',          value: polizza.agente || '—' },
            { label: 'Società',         value: polizza.societa_nome },
            { label: 'Sedi coperte',    value: (polizza.sediDettaglio || []).map((s) => s.sede_nome).join(', ') || '—' },
            { label: 'Stato',           value: polizza.stato ? (
              <span className={`bop-badge ${STATO_BADGE[polizza.stato]}`}>{polizza.stato}</span>
            ) : '—' },
            { label: 'Data inizio',     value: fmtDate(polizza.dataInizio) },
            { label: 'Data scadenza',   value: fmtDate(polizza.dataFine) },
            { label: 'Tacito rinnovo',  value: polizza.tacitaRinnovo ? 'Sì' : 'No' },
            { label: 'Importo totale',  value: fmtEuro(polizza.importoTotale) },
            { label: 'Note',            value: polizza.note || '—' },
          ].map((row, i) => (
            <div key={i}>
              <div className="bop-polizza-det__detail-label">
                {row.label}
              </div>
              <div className="bop-polizza-det__detail-value">{row.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Rischi per sede ──────────────────────────────────────────────── */}
      {(polizza.sediDettaglio || []).map((sede) => (
        <div className="bop-card bop-polizza-det__card-mb" key={sede.sedeId}>
          <div className="bop-polizza-det__section-header">
            <strong className="bop-polizza-det__section-title">{sede.sede_nome}</strong>
          </div>
          <div className="bop-table-wrapper">
            {sede.rischi.length === 0 ? (
              <div className="bop-empty-state bop-polizza-det__empty-padded">
                Nessun rischio per questa sede.
              </div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Rischio</th>
                    <th>Descrizione</th>
                    <th className="bop-text-right bop-polizza-det__th-massimale">Massimale</th>
                  </tr>
                </thead>
                <tbody>
                  {sede.rischi.map((r, idx) => (
                    <tr key={`${r.societa_sederischio_id}-${idx}`}>
                      <td>
                        <strong>{r.rischio_nome}</strong>
                        {r.sedeId === null && (
                          <span className="bop-polizza-det__tag-generale">(generale)</span>
                        )}
                      </td>
                      <td className="bop-text-muted">{r.rischio_descrizione || '—'}</td>
                      <td className="bop-text-right bop-text-muted">
                        {r.massimale != null ? fmtEuro(r.massimale) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ))}

      {/* ── Allegati ─────────────────────────────────────────────────────── */}
      <div className="bop-card">
        <div className="bop-polizza-det__section-header bop-polizza-det__section-header--border">
          <strong className="bop-polizza-det__section-title">Allegati</strong>
        </div>
        <div className="bop-table-wrapper">
          {!polizza.allegati || polizza.allegati.length === 0 ? (
            <div className="bop-empty-state">Nessun allegato caricato.</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Nome file</th>
                  <th>Caricato il</th>
                  <th className="bop-polizza-det__th-download"></th>
                </tr>
              </thead>
              <tbody>
                {polizza.allegati.map((a: AllegatoPolizza) => (
                  <tr key={a.id}>
                    <td>{a.nome}</td>
                    <td className="bop-polizza-det__date-cell">
                      {a.createdAt ? new Date(a.createdAt).toLocaleString('it-IT') : '—'}
                    </td>
                    <td>
                      {/* @api GET /api/assicurazioni/polizze/:id/allegati/:allegatoId/download -> file stream (attachment) */}
                      <button
                        type="button"
                        className="bop-btn bop-btn-ghost bop-polizza-det__download-link"
                        onClick={() => apiDownload(`/api/assicurazioni/polizze/${polizza.id}/allegati/${a.id}/download`, a.nome || 'allegato')}
                      >
                        ↓ Scarica
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showWizard && initialWizard && (
        <PolizzaWizard
          mode="edit"
          polizzaId={polizza.id}
          initial={initialWizard}
          existingAllegati={polizza.allegati || []}
          societa={societa}
          sediAll={sediAll}
          onSaved={handleSaved}
          onClose={() => setShowWizard(false)}
        />
      )}
    </Layout>
  );
}
